/**
 * Покрытие данных: за какие месяцы в выгрузке из 1С вообще есть документы.
 *
 * Ноль и «нет данных» — разные вещи. Месяц, за который 1С ещё ничего
 * не прислала, нельзя рисовать нулевым столбцом и нельзя брать в сравнение
 * «3 месяца к 3»: падение на 60% там, где просто не доехала выгрузка, —
 * выдуманная цифра. Поэтому каждый раздел знает, какие месяцы загружены,
 * и отличает их от закрытых правами (окно «последние полгода»).
 *
 * Покрытие считается одним запросом по месяцам за четыре года без фильтра
 * города: отсутствие продаж в одном городе — честный ноль, отсутствие
 * продаж во всей доступной выдаче — отсутствие данных. Тело запроса
 * одинаковое у всех разделов, поэтому ответ берётся из общего кеша.
 *
 * Граница называется датой последнего документа, если сервер её знает
 * (meta.last_document_date): «продажи по 26.08.2026». data_as_of для этого
 * не годится — это время выгрузки, а не дата документа. Если последний
 * документ не в последний день месяца, месяц помечается возможно неполным:
 * 1С могла выгрузить его не до конца, и сравнивать его с целым нельзя.
 * Старый сервер даты не присылает — тогда граница называется месяцем.
 */

import { useMemo } from 'react'
import { endOfCurrentMonth, formatDate, monthsAgo } from '../format'
import { num, useReport } from './data'
import { labelOf, monthLabel, monthsOf, shortMonth, todayIso, type DateRange, type PeriodSelection } from './period'

export interface Coverage {
  /** Покрытие известно: запрос выполнен. Пока нет — ничего не утверждаем. */
  known: boolean
  /** Первые числа месяцев, за которые есть хоть одна строка с ненулевым показателем. */
  months: Set<string>
  first: string | null
  last: string | null
  /** Дата последнего документа (YYYY-MM-DD), если сервер её прислал. */
  lastDate: string | null
  /** Последний месяц, возможно, загружен не целиком: последний документ — не в последний день. */
  lastPartial: boolean
  /** Права открывают данные начиная с этой даты (YYYY-MM-DD). */
  notBefore: string | null
}

const WINDOW_MONTHS = 48

export const EMPTY_COVERAGE: Coverage = { known: false, months: new Set(), first: null, last: null, lastDate: null, lastPartial: false, notBefore: null }

/** Тело запроса покрытия — одно на всё приложение, чтобы работал кеш. */
export function coverageBody(metric: string) {
  return { period: { from: monthsAgo(WINDOW_MONTHS), to: endOfCurrentMonth() }, group_by: ['period'], metrics: [metric] }
}

export function useCoverage(code: string | null, metric: string, notBefore: string | null): Coverage {
  const state = useReport(code ?? 'margin_by_department', code ? coverageBody(metric) : null)
  const data = code && state.status === 'ready' ? state.data : null
  return useMemo(() => {
    // Отчёт закрыт или упал — покрытие неизвестно, разделы работают как раньше.
    if (!data) return { ...EMPTY_COVERAGE, notBefore }
    const months = new Set(
      data.rows.filter((row) => num(row[metric]) !== 0).map((row) => `${String(row.period).slice(0, 7)}-01`),
    )
    const sorted = [...months].sort()
    const narrowedFrom = data.meta.period_applied.narrowed ? data.meta.period_applied.from : null
    const last = sorted[sorted.length - 1] ?? null
    const rawDate = data.meta.last_document_date ?? null
    // Дата относится к последнему загруженному месяцу — иначе ей не верим.
    const lastDate = rawDate && last && rawDate.slice(0, 7) === last.slice(0, 7) ? rawDate.slice(0, 10) : null
    return {
      known: true,
      months,
      first: sorted[0] ?? null,
      last,
      lastDate,
      lastPartial: Boolean(lastDate && last && lastDate < monthEnd(last)),
      // Помесячный отчёт режет окно прав по границе месяца: при праве «с 27.04»
      // сервер отдаёт данные с 01.05, и апрель — закрыт правами, а не «не загружен».
      notBefore: narrowedFrom && (!notBefore || narrowedFrom > notBefore) ? narrowedFrom : notBefore,
    }
  }, [data, metric, notBefore])
}

// --- Месяцы -------------------------------------------------------------------

export type MonthState = 'data' | 'nodata' | 'closed'

/** Состояние месяца: есть данные, не загружен или закрыт правами. */
export function monthState(month: string, coverage: Coverage): MonthState {
  if (!coverage.known) return 'data'
  if (coverage.notBefore && monthEnd(month) < coverage.notBefore) return 'closed'
  return coverage.months.has(month) ? 'data' : 'nodata'
}

function monthEnd(month: string): string {
  const year = Number(month.slice(0, 4))
  const index = Number(month.slice(5, 7))
  return new Date(Date.UTC(year, index, 0)).toISOString().slice(0, 10)
}

/** «по 26.08.2026», а без даты от сервера — «по август 2026». */
export function lastLabel(coverage: Coverage): string {
  if (coverage.lastDate) return formatDate(coverage.lastDate)
  return coverage.last ? monthLabel(coverage.last) : ''
}

/** Месяц загружен, но, возможно, не до конца: последний документ раньше последнего дня. */
export function isPartialMonth(month: string, coverage: Coverage): boolean {
  return coverage.lastPartial && coverage.last === month && monthEnd(month) < todayIso()
}

/** Значение месяца или null, если месяц не загружен или закрыт: на графике — пропуск, а не ноль. */
export function valueIn(month: string, coverage: Coverage, value: unknown): number | null {
  return monthState(month, coverage) === 'data' ? num(value) : null
}

/** Для графика: месяц без данных — пропуск с причиной, загруженный — undefined. */
export function missingOf(month: string, coverage: Coverage): 'nodata' | 'closed' | undefined {
  const state = monthState(month, coverage)
  return state === 'data' ? undefined : state
}

/** «июль–декабрь 2026», «июнь 2026», «ноя 2025 – фев 2026». */
export function monthSpan(months: string[]): string {
  if (months.length === 0) return ''
  const first = months[0]!
  const last = months[months.length - 1]!
  if (first === last) return monthLabel(first)
  if (first.slice(0, 4) === last.slice(0, 4)) return `${monthLabel(first).split(' ')[0]}–${monthLabel(last)}`
  return `${shortMonth(first)} ${first.slice(0, 4)} – ${shortMonth(last)} ${last.slice(0, 4)}`
}

/** Короткая подпись окна для «3 к 3»: «апр–июн». */
function shortSpan(months: string[]): string {
  if (months.length === 0) return ''
  const first = months[0]!
  const last = months[months.length - 1]!
  return first === last ? shortMonth(first) : `${shortMonth(first)}–${shortMonth(last)}`
}

/** Следующий месяц: '2026-09-01' → '2026-10-01'. */
function nextMonth(month: string): string {
  const year = Number(month.slice(0, 4))
  const index = Number(month.slice(5, 7))
  return new Date(Date.UTC(year, index, 1)).toISOString().slice(0, 10)
}

/**
 * Окна «последние 3 месяца к предыдущим 3» только по загруженным
 * и закончившимся месяцам. Хвост без выгрузки отбрасывается, текущий
 * месяц тоже: он ещё идёт, и «3 к 3» с неполным октябрём показало бы
 * падение, которого нет. Шесть месяцев должны идти подряд: «июн–авг
 * к фев–апр» с дырой в мае — не сравнение двух соседних кварталов.
 * Если так не набирается — null, и строка пишет, что сравнение не считается.
 */
export function recentWindows(months: string[], coverage: Coverage): { recent: string[]; prior: string[]; label: string } | null {
  const today = todayIso()
  // Месяц, выгруженный не до конца, в «3 к 3» не берётся: сравнивать его с целыми — придумать падение.
  const loaded = months.filter(
    (month) => monthState(month, coverage) === 'data' && monthEnd(month) < today && !isPartialMonth(month, coverage),
  )
  if (loaded.length < 6) return null
  const six = loaded.slice(-6)
  if (six.some((month, index) => index > 0 && nextMonth(six[index - 1]!) !== month)) return null
  const recent = six.slice(3)
  const prior = six.slice(0, 3)
  return { recent, prior, label: `${shortSpan(recent)} к ${shortSpan(prior)}` }
}

// --- Период целиком ------------------------------------------------------------

export interface PeriodGap {
  kind: 'after' | 'before' | 'closed' | 'hole'
  title: string
  hint: string
  /** Куда предложить перейти. */
  target: PeriodSelection | null
  targetLabel: string | null
}

const monthSelection = (month: string): PeriodSelection => ({ grain: 'month', start: month })

/**
 * Почему за период нет данных — если их нет совсем. null — данные есть
 * хотя бы за один месяц периода (или покрытие неизвестно).
 *
 * `subject` — «продажи» или «движения денег»: в тексте называется то,
 * чего нет, а не абстрактные «данные».
 */
export function gapOf(selection: PeriodSelection, range: DateRange, coverage: Coverage, subject = 'продажи'): PeriodGap | null {
  if (!coverage.known) return null
  const months = monthsOf(range)
  const states = months.map((month) => monthState(month, coverage))
  if (states.includes('data')) return null

  const label = labelOf(selection)
  const Subject = subject.charAt(0).toUpperCase() + subject.slice(1)

  if (states.every((state) => state === 'closed') && coverage.notBefore) {
    const first = [...coverage.months].sort().find((month) => monthEnd(month) >= coverage.notBefore!) ?? null
    return {
      kind: 'closed',
      title: `Вам доступны данные с ${formatDate(coverage.notBefore)}`,
      hint: `Период «${label}» раньше окна, которое открывают ваши права. Расширить окно может администратор.`,
      target: first ? monthSelection(first) : null,
      targetLabel: first ? monthLabel(first) : null,
    }
  }

  if (!coverage.last) {
    return {
      kind: 'hole',
      title: `${Subject} в выгрузке пока нет`,
      hint: 'Из 1С ещё не пришло ни одного документа за последние четыре года.',
      target: null,
      targetLabel: null,
    }
  }

  if (range.from > coverage.last) {
    return {
      kind: 'after',
      title: `Последние ${subject} — ${coverage.lastDate ? formatDate(coverage.lastDate) : monthLabel(coverage.last)}`,
      hint: `За «${label}» документов в выгрузке из 1С ещё нет. Это не ноль: месяц просто не загружен.`,
      target: monthSelection(coverage.last),
      targetLabel: monthLabel(coverage.last),
    }
  }

  if (coverage.first && range.to < coverage.first) {
    return {
      kind: 'before',
      title: `${Subject} загружены с ${monthLabel(coverage.first)}`,
      hint: `Более ранних документов, чем ${monthLabel(coverage.first)}, в выгрузке нет.`,
      target: monthSelection(coverage.first),
      targetLabel: monthLabel(coverage.first),
    }
  }

  return {
    kind: 'hole',
    title: `За «${label}» ${subject} в выгрузке нет`,
    hint: `Соседние месяцы загружены, этот — нет. Последние ${subject} — ${monthLabel(coverage.last)}.`,
    target: monthSelection(coverage.last),
    targetLabel: monthLabel(coverage.last),
  }
}

/**
 * Период загружен не целиком: какие месяцы не входят в итог и почему.
 * Например, «2026 год» при выгрузке по июнь — «июль–декабрь 2026 не загружены».
 */
export function partialNote(range: DateRange, coverage: Coverage, subject = 'продажи'): string | null {
  if (!coverage.known) return null
  const months = monthsOf(range)
  const states = months.map((month) => monthState(month, coverage))
  if (!states.includes('data')) return null
  const nodata = months.filter((_, index) => states[index] === 'nodata')
  const closed = months.filter((_, index) => states[index] === 'closed')
  const parts: string[] = []
  if (nodata.length > 0) parts.push(`${monthSpan(nodata)} — ${subject} не загружены`)
  if (closed.length > 0 && coverage.notBefore) parts.push(`до ${formatDate(coverage.notBefore)} — закрыто вашими правами`)
  const partial = months.find((month) => isPartialMonth(month, coverage))
  if (parts.length === 0 && partial) {
    return `${monthLabel(partial)}, возможно, загружен не целиком: последний документ — ${formatDate(coverage.lastDate!)}. Сравнение с прошлым периодом не показывается.`
  }
  if (partial) parts.push(`${monthLabel(partial)} — по ${formatDate(coverage.lastDate!)}, возможно, не целиком`)
  if (parts.length === 0) return null
  return `В периоде есть месяцы без данных: ${parts.join('; ')}. Итоги — только по загруженным месяцам, сравнение с прошлым периодом не показывается.`
}

/** Есть ли в периоде незагруженные или закрытые месяцы: тогда сравнение нечестно. */
export function hasMissingMonths(range: DateRange, coverage: Coverage): boolean {
  if (!coverage.known) return false
  // Месяц, обрезанный окном прав посередине, тоже неполный: сервер отдаёт
  // только его хвост, и сравнение с целым месяцем показало бы падение.
  if (coverage.notBefore && range.from < coverage.notBefore) return true
  return monthsOf(range).some((month) => monthState(month, coverage) !== 'data' || isPartialMonth(month, coverage))
}

/** Выгрузка отстаёт: последних продаж нет даже за прошлый месяц. */
export function lagsBehind(coverage: Coverage, reference: string): boolean {
  return Boolean(coverage.known && coverage.last && coverage.last < reference)
}
